import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authResetPasswordPublic } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";

/**
 * Landing page for the reset link the user gets by email.
 * URL shape: /admin/reset-password?token=<url_safe_32_bytes>
 * Ask for the email again + new password, submit to the backend.
 */
export default function AdminResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const emailFromLink = params.get("email") || "";
  const [email, setEmail] = useState(emailFromLink);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (newPw.length < 8) return setError("Password must be at least 8 characters.");
    if (newPw !== confirmPw) return setError("Passwords don't match.");
    if (!email) return setError("Please enter the email tied to this reset link.");
    setBusy(true); setError(null);
    try {
      await authResetPasswordPublic(email.trim(), token, newPw, "email");
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not reset. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageWrap} data-testid="admin-reset-page">
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · ADMIN PASSWORD RESET</div>
        <h1 style={h1}>{done ? "PASSWORD UPDATED" : "SET A NEW PASSWORD"}</h1>

        {!done && (
          <form onSubmit={submit}>
            <Label>ADMIN EMAIL</Label>
            <input type="email" required autoFocus value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   data-testid="reset-email-input" style={inputStyle}
                   placeholder="you@yourcompany.com" />

            <Label>NEW PASSWORD (min 8 chars)</Label>
            <input type="password" required minLength={8} value={newPw}
                   onChange={(e) => setNewPw(e.target.value)}
                   data-testid="reset-new-password-input" style={inputStyle} />

            <Label>CONFIRM NEW PASSWORD</Label>
            <input type="password" required minLength={8} value={confirmPw}
                   onChange={(e) => setConfirmPw(e.target.value)}
                   data-testid="reset-confirm-password-input" style={inputStyle} />

            {error && <p data-testid="reset-error" style={errBox}>{error}</p>}
            <button type="submit" disabled={busy || !token}
                    data-testid="reset-submit-button"
                    style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Resetting…" : "SAVE NEW PASSWORD →"}
            </button>
          </form>
        )}

        {done && (
          <>
            <p style={{ color: "#1A7A4A", fontWeight: 700 }} data-testid="reset-done-info">
              ✓ Your password has been updated. You can now sign in with your new password.
            </p>
            <button onClick={() => navigate("/admin/login")} style={btnPrimary} data-testid="reset-signin-button">
              GO TO SIGN-IN →
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{children}</div>
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const card = { background: "#fff", borderRadius: 18, padding: "40px 36px", maxWidth: 460, width: "100%", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 34, color: NAVY, margin: "4px 0 22px" };
const inputStyle = { width: "100%", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, marginBottom: 18, outline: "none", boxSizing: "border-box" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 4 };
const errBox = { color: "#A32D2D", background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
